/**
 * platform.js — rilevamento piattaforma/browser per l'installazione PWA.
 *
 * Centralizza la logica prima sparsa tra InstallPrompt e InstallGuideScreen e
 * aggiunge il pezzo mancante: il riconoscimento delle WEBVIEW IN-APP
 * (WhatsApp, Instagram, Facebook, ...). Su iPhone quelle NON possono
 * "Aggiungi alla schermata Home" — l'unico browser che lo permette è Safari
 * "vero". Chi apre un invito Nullum da WhatsApp o scansiona un QR e finisce in
 * una webview deve prima APRIRE IN SAFARI: senza dirglielo, resta bloccato.
 */

const ua = () => (typeof navigator !== 'undefined' && navigator.userAgent) || '';

/** Vero se giriamo dentro l'app NATIVA Capacitor (Android/iOS), non nel browser/PWA.
 *  In nativo non ha senso mostrare la guida "installa sul telefono": è già installata. */
export function isNativeApp() {
  try {
    return !!(typeof window !== 'undefined' && window.Capacitor &&
      (typeof window.Capacitor.isNativePlatform === 'function'
        ? window.Capacitor.isNativePlatform()
        : window.Capacitor.platform && window.Capacitor.platform !== 'web'));
  } catch { return false; }
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator.standalone === true
  );
}

export function isIOS() {
  const u = ua();
  const iOSDevice = /iPad|iPhone|iPod/.test(u);
  // iPadOS 13+ si maschera da Mac desktop: distinguilo dal touch.
  const iPadOS =
    u.includes('Macintosh') &&
    typeof document !== 'undefined' &&
    'ontouchend' in document;
  return iOSDevice || iPadOS;
}

export function isAndroid() {
  return /Android/i.test(ua());
}

/**
 * Nome "umano" dell'app host se siamo dentro la sua webview in-app, altrimenti
 * null. Le webview ereditano lo user-agent di Safari (quindi sembrano Safari):
 * l'unico segnale affidabile è il marker dell'app appeso in fondo all'UA.
 */
export function inAppBrowser() {
  const u = ua();
  if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(u)) return 'Facebook';
  if (/Instagram/i.test(u)) return 'Instagram';
  if (/Messenger/i.test(u)) return 'Messenger';
  if (/WhatsApp/i.test(u)) return 'WhatsApp';
  if (/Line\//i.test(u)) return 'LINE';
  if (/Telegram/i.test(u)) return 'Telegram';
  if (/BytedanceWebview|musical_ly|TikTok|Bytedance/i.test(u)) return 'TikTok';
  if (/Twitter/i.test(u)) return 'X';
  if (/LinkedInApp/i.test(u)) return 'LinkedIn';
  if (/Snapchat/i.test(u)) return 'Snapchat';
  if (/Pinterest/i.test(u)) return 'Pinterest';
  if (/GSA\//i.test(u)) return 'Google';
  return null;
}

/**
 * Browser iOS reali ma NON Safari (Chrome/Firefox/Edge/Opera): su iOS NON
 * possono installare PWA — solo Safari può. Ritorna il nome o null.
 */
export function iosOtherBrowser() {
  const u = ua();
  if (/CriOS/i.test(u)) return 'Chrome';
  if (/FxiOS/i.test(u)) return 'Firefox';
  if (/EdgiOS/i.test(u)) return 'Edge';
  if (/OPiOS|OPT\//i.test(u)) return 'Opera';
  return null;
}

/** Safari "vero" su iOS: l'unico contesto da cui si può Aggiungi-a-Home. */
export function isIOSSafari() {
  if (!isIOS()) return false;
  if (inAppBrowser()) return false;
  if (iosOtherBrowser()) return false;
  const u = ua();
  // Safari vero espone sia "Safari" sia "Version/". (I marker app-specifici
  // sono già stati esclusi sopra, così IG/FB/... — che mimano l'UA Safari —
  // non passano di qui.)
  return /Safari/i.test(u) && /Version\//i.test(u);
}

/**
 * Sintesi per i componenti UI. Un solo punto di verità.
 */
export function installContext() {
  const standalone = isStandalone();
  const ios = isIOS();
  const android = isAndroid();
  const inApp = ios ? inAppBrowser() : null;
  const otherBrowser = ios ? iosOtherBrowser() : null;
  const iosSafari = isIOSSafari();
  return {
    standalone,
    ios,
    android,
    desktop: !ios && !android,
    inApp, // nome app host o null
    otherBrowser, // 'Chrome'/'Firefox'/... o null
    iosSafari, // Safari vero su iOS
    // Su iOS si installa SOLO da Safari vero.
    iosCanInstall: ios && iosSafari && !standalone,
    // iOS ma in un contesto da cui NON si può installare → serve "apri in Safari".
    iosNeedsSafari: ios && !standalone && (!!inApp || !!otherBrowser || !iosSafari),
  };
}
